import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { DataGridPro, Mui, MuiDataGrid } from '@leap4/frontend';
import { deleteArchivedPublishers, errorMessage, fetchArchivedPublishers, restorePublisher } from './appAdsLines.api';
import { searchRows } from './rowSearch';
import { useWrappingGrid } from './wrappingGrid';
import { dateToStringFormat } from '../../utils/functions';

const { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } = Mui;

export const ArchivedPublishers: React.FC = () => {
    const client = useQueryClient();
    const [search, setSearch] = useState('');
    const wrapping = useWrappingGrid();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [confirmIds, setConfirmIds] = useState<string[] | null>(null);
    const [successMessage, setSuccessMessage] = useState('');
    const { data = [], isLoading, error, refetch } = useQuery('archivedPublishers', fetchArchivedPublishers, {
        refetchOnWindowFocus: false,
    });
    const refresh = () => {
        ['archivedPublishers', 'trackedPublishers', 'trackedPublisher', 'trackedLines', 'distinctLineSpecs', 'oldLines']
            .forEach(key => client.invalidateQueries(key));
    };
    const restore = useMutation(({ id }: { id: string; name: string }) => restorePublisher(id), {
        onSuccess: (_result, { id, name }) => {
            setSuccessMessage(`Publisher '${name}' restored. Find it in the Publishers tab.`);
            setSelectedIds(ids => ids.filter(selected => selected !== id));
            refresh();
        },
    });
    const remove = useMutation(deleteArchivedPublishers, {
        onSuccess: (result) => {
            setSelectedIds([]);
            setConfirmIds(null);
            setSuccessMessage(`Permanently deleted ${result.deleted} archived publisher(s).` +
                (result.skipped ? ` ${result.skipped} no longer available for deletion; refresh and try again if needed.` : ''));
            refresh();
        },
        // A batch can partially finish before a database error. Keep the selection for retry,
        // but refresh the archive so already-deleted rows disappear.
        onError: () => client.invalidateQueries('archivedPublishers'),
    });
    const busy = restore.isLoading || remove.isLoading;
    const columns: MuiDataGrid.GridColDef[] = [
        { field: 'name', headerName: 'Publisher', width: 240 },
        { field: 'domain', headerName: 'Domain', width: 250 },
        { field: 'archivedAt', headerName: 'Archived', width: 190,
            valueGetter: (params: any) => params.row.archivedAt ? dateToStringFormat(params.row.archivedAt) : 'Previously deleted' },
        { field: 'actions', headerName: 'Actions', width: 150, sortable: false, filterable: false,
            renderCell: (params: any) => <Button disabled={busy || !!confirmIds}
                aria-label={`Restore ${params.row.name}`} onClick={() => { setSuccessMessage(''); restore.mutate({ id: params.row._id, name: params.row.name }); }}>
                {restore.isLoading && restore.variables?.id === params.row._id ? 'Restoring…' : 'Restore'}
            </Button> },
    ];
    return <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
            Archived publishers keep their settings, lines and history. Restore returns them to the publishers list.
            Previously deleted publishers that are still stored also appear here.
        </Alert>
        {!!successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {!!remove.error && !confirmIds && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(remove.error)}</Alert>}
        {!!restore.error && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(restore.error)}</Alert>}
        {!!error && <Alert severity="error" action={<Button onClick={() => refetch()}>Retry</Button>}>{errorMessage(error)}</Alert>}
        <Box display="flex" justifyContent="flex-start" alignItems="center" gap={2} flexWrap="wrap" sx={{ width: '100%', mb: 2 }}>
            <TextField label="Search archive" size="small" value={search} onChange={e => setSearch(e.target.value)}
                sx={{ width: 320, maxWidth: '100%' }} />
            <Button color="error" variant="outlined" disabled={!selectedIds.length || busy}
                onClick={() => { remove.reset(); setSuccessMessage(''); setConfirmIds([...selectedIds]); }}>
                Delete selected ({selectedIds.length})
            </Button>
        </Box>
        <DataGridPro {...wrapping.gridProps}
            rows={searchRows(data, search, publisher => [publisher.name, publisher.domain])}
            columns={columns} loading={isLoading} getRowId={row => row._id} autoHeight pagination
            pageSize={100} rowsPerPageOptions={[25, 50, 100]} disableColumnPinning disableRowSelectionOnClick
            checkboxSelection checkboxSelectionVisibleOnly disableRowSelectionExcludeModel
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={ids => { if (!busy && !confirmIds) setSelectedIds(ids.map(String)); }}
            style={{ minHeight: '40vh' }} />
        {!isLoading && !error && !data.length && <Alert severity="info">No archived publishers.</Alert>}
        <Dialog open={!!confirmIds} onClose={() => { if (!remove.isLoading) setConfirmIds(null); }} fullWidth maxWidth="sm">
            <DialogTitle>Permanently delete selected publishers?</DialogTitle>
            <DialogContent>
                Delete these {confirmIds?.length ?? 0} archived publisher(s), including all their lines and change history?
                This cannot be undone, and they can no longer be restored.
                {!!remove.error && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(remove.error)} You can retry the deletion.</Alert>}
            </DialogContent>
            <DialogActions>
                <Button autoFocus disabled={remove.isLoading} onClick={() => setConfirmIds(null)}>Cancel</Button>
                <Button color="error" disabled={remove.isLoading || !confirmIds?.length}
                    onClick={() => { if (confirmIds?.length && !remove.isLoading) remove.mutate(confirmIds); }}>
                    {remove.isLoading ? 'Deleting…' : 'Delete permanently'}
                </Button>
            </DialogActions>
        </Dialog>
    </Box>;
};
